<template>
    <div class="box kb-channel-card">
        <div class="kb-channel-card__row">
            <div class="kb-channel-card__icon">{{ initial }}</div>
            <div class="kb-channel-card__main">
                <div class="kb-channel-card__head">
                    <span class="kb-channel-card__name">{{ connection.channel_name }}</span>
                    <span class="kb-channel-card__type">{{ typeLabel }}</span>
                    <span class="kb-channel-card__connected">
                        {{ $t('knowledgebase.channelConnected') }}
                        <kb-time :value="connection.connected_at" />
                    </span>
                </div>

                <div class="kb-channel-card__sync kb-channel-card__sync--pending"
                     v-if="resyncScheduled">
                    {{ $t('knowledgebase.channelResyncScheduled') }}
                </div>
                <div class="kb-channel-card__sync" v-else-if="connection.last_sync_error">
                    <strong>{{ $t('knowledgebase.channelLastSyncFailed') }}</strong>
                    <span class="kb-channel-card__sync-error">{{ connection.last_sync_error }}</span>
                    <button
                        type="button"
                        class="button is-small is-outlined kb-channel-card__sync-retry"
                        :disabled="resyncPending"
                        @click="resync"
                    >
                        {{ $t('knowledgebase.channelResync') }}
                    </button>
                </div>
                <div class="kb-channel-card__sync kb-channel-card__sync--ok"
                     v-else-if="connection.last_synced_at">
                    {{ $t('knowledgebase.channelLastSynced') }}
                    <kb-time :value="connection.last_synced_at" />
                </div>
                <div class="kb-channel-card__sync kb-channel-card__sync--pending" v-else>
                    {{ $t('knowledgebase.channelNeverSynced') }}
                </div>

                <div class="kb-channel-card__visibility">
                    <span class="kb-channel-card__visible-count">{{ visibleCount }}</span>
                    {{ $t('knowledgebase.channelVisibilitySummary', {total: totalPublished}) }}
                    <span class="kb-channel-card__hidden" v-if="excludedCount > 0">
                        · {{ $tc('knowledgebase.channelHiddenCount', excludedCount, {count: excludedCount}) }}
                    </span>
                </div>
            </div>
            <div class="kb-channel-card__actions">
                <button class="button is-small is-outlined" @click="$emit('configure', connection)">
                    {{ $t('knowledgebase.channelConfigure') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import Api from '../../../api/Api';
import KbTime from './kb-time.vue';

export default {
    name: 'kb-channel-card',

    components: {KbTime},

    props: {
        connection: {type: Object, required: true},
        // The dashboard counts visibility against the campground's *published*
        // article set — drafts are invisible everywhere and don't count toward
        // a channel's coverage. Parent passes the precomputed counts so the
        // card doesn't have to reach into the data tree itself.
        totalPublished: {type: Number, required: true},
        excludedCount: {type: Number, required: true},
    },

    data() {
        return {
            // Local "queued, come back in a minute" acknowledgement. Mirrors
            // the same state in the configure modal — no polling, the user
            // refreshes to see the result.
            resyncPending: false,
            resyncScheduled: false,
        };
    },

    computed: {
        initial() {
            const name = this.connection.channel_name || '';
            return (name[0] || '?').toUpperCase();
        },
        typeLabel() {
            // The chip in the prototype is "PUSH" vs "FETCH" — derived from
            // integration_type. For now there's only push (synthflow), but
            // the helper is here for when the public_faq / fetch types land.
            const t = this.connection.integration_type;
            if(t === 'synthflow') return this.$t('knowledgebase.channelTypePush');
            return this.$t('knowledgebase.channelTypeFetch');
        },
        visibleCount() {
            return Math.max(0, this.totalPublished - this.excludedCount);
        },
    },

    methods: {
        async resync() {
            if(this.resyncPending) return;
            this.resyncPending = true;
            try {
                await Api.knowledgebase.resyncConnection(this.connection.id);
                this.resyncScheduled = true;
            } finally {
                this.resyncPending = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.kb-channel-card {
    margin-bottom: 10px;

    &__row {
        display: flex;
        align-items: flex-start;
        gap: 14px;
    }

    &__icon {
        width: 42px;
        height: 42px;
        border-radius: 4px;
        background: #eaf3f8;
        color: #357;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
        font-size: 1.1rem;
        flex-shrink: 0;
    }

    &__main {
        flex: 1;
        min-width: 0;
    }

    &__head {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    &__name {
        font-weight: 700;
        font-size: 1rem;
    }

    &__type {
        font-size: 0.7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #666;
        background: #f3f3f3;
        border-radius: 3px;
        padding: 2px 6px;
    }

    &__connected {
        font-size: 0.78rem;
        color: #888;
    }

    &__sync {
        font-size: 0.8rem;
        margin-top: 4px;
        color: #888;

        &--ok {
            color: #4a7;
        }

        &--pending {
            color: #888;
            font-style: italic;
        }
    }

    &__sync-error {
        margin-left: 8px;
        color: #c33;
    }

    &__sync-retry {
        display: inline-block;
        margin-left: 8px;
    }

    &__visibility {
        font-size: 0.85rem;
        color: #444;
        margin-top: 6px;
    }

    &__visible-count {
        font-weight: 700;
        color: #222;
    }

    &__hidden {
        color: #a76;
    }

    &__actions {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex-shrink: 0;
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-channel-card.vue